import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Award, Users, Shield, TrendingUp } from 'lucide-react';

export const AboutPage = () => {
  const values = [
    {
      icon: <Shield size={32} />,
      title: 'Transparency',
      description: 'We operate with complete transparency and adhere to Canadian education regulations'
    },
    {
      icon: <Award size={32} />,
      title: 'Excellence',
      description: 'Committed to delivering exceptional guidance and support for every student'
    },
    {
      icon: <Users size={32} />,
      title: 'Student-Centric',
      description: 'Your success and satisfaction are at the heart of everything we do'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Results-Driven',
      description: '95% success rate in college admissions and student placements'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">About ELARA</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="about-title">
              Guiding Students to Educational Excellence
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300">
              ELARA Education is a premier education consulting company in Canada, dedicated to helping 
              both international and domestic students achieve their academic dreams.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 border-l-2 border-secondary"
              data-testid="mission-card"
            >
              <div className="text-secondary mb-4">
                <Target size={40} />
              </div>
              <h2 className="text-3xl font-semibold mb-4">Our Mission</h2>
              <p className="text-slate-600 leading-relaxed">
                To guide students through the right education pathway according to Canadian education policies 
                and regulations, ensuring transparency, compliance, and student success at every step. We believe 
                in empowering students with knowledge and support to make informed decisions about their future.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 border-l-2 border-secondary"
              data-testid="vision-card"
            >
              <div className="text-secondary mb-4">
                <Eye size={40} />
              </div>
              <h2 className="text-3xl font-semibold mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed">
                To become Canada's most trusted education consulting partner, recognized for our integrity, 
                expertise, and commitment to student success. We envision a future where every student has 
                access to quality education and personalized guidance to achieve their career aspirations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&q=80"
                alt="ELARA Education Story"
                className="w-full h-[500px] object-cover"
                data-testid="story-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Our Story</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="story-title">
                A Decade of Trust and Excellence
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Today, we've helped over 1000 students achieve their educational dreams, with a 98% success rate 
                  in college admissions and enrollment across 120+ partner colleges.
                </p>
                <p>
                  Our team of experienced education consultants works closely with students, providing personalized 
                  guidance from application to admission and beyond. We take pride in our ethical approach and commitment to transparency.
                </p>
                <p>
                  Today, we've helped over 1000 students achieve their educational dreams, with a 98% success rate 
                  in college admissions. But our work doesn't stop at admission—we continue to support our students 
                  throughout their academic journey.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-32 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Our Values</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="values-title">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8"
                data-testid={`value-card-${index}`}
              >
                <div className="text-secondary mb-4">{value.icon}</div>
                <h3 className="text-2xl font-semibold mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary text-white" data-testid="compliance-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Shield className="text-secondary mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-semibold mb-4">Registered Education Consulting Company</h3>
            <p className="text-slate-300 leading-relaxed max-w-3xl mx-auto">
              ELARA Education is a Registered Education Consulting Company operating in full compliance with Canadian education regulations. 
              We specialize in education guidance and college admissions support. For visa and immigration services, 
              we recommend consulting a Regulated Canadian Immigration Consultant (RCIC).
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};